import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, Plus, AlertTriangle, Loader2, Save, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = {
  medicament: "Médicament",
  pansement: "Pansement",
  materiel: "Matériel",
  antiseptique: "Antiseptique",
  autre: "Autre",
};

export default function PharmaciePage() {
  const [items, setItems] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openMove, setOpenMove] = useState(false);
  const [moveItem, setMoveItem] = useState(null);
  const [moveForm, setMoveForm] = useState({ type: "entree", qty: 1, reason: "" });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", category: "medicament", qty: 0, unit: "comprimé", expiry_date: "", alert_threshold: 5, notes: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/infirmary/pharmacy");
      setItems(data);
    } catch {
      toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Nom requis");
    setSaving(true);
    try {
      await api.post("/infirmary/pharmacy", { ...form, qty: Number(form.qty), alert_threshold: Number(form.alert_threshold) });
      toast.success("Article ajouté");
      setOpenAdd(false);
      setForm({ name: "", category: "medicament", qty: 0, unit: "comprimé", expiry_date: "", alert_threshold: 5, notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const openMovement = (item, type) => {
    setMoveItem(item);
    setMoveForm({ type, qty: 1, reason: "" });
    setOpenMove(true);
  };

  const doMovement = async () => {
    if (moveForm.qty <= 0) return toast.error("Quantité invalide");
    setSaving(true);
    try {
      const { data } = await api.post("/infirmary/pharmacy/movement", {
        item_id: moveItem.id,
        type: moveForm.type,
        qty: Number(moveForm.qty),
        reason: moveForm.reason,
      });
      toast.success(`Stock mis à jour : ${data.new_qty} ${moveItem.unit}`);
      setOpenMove(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pharmacie-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Package className="h-3 w-3" /><span>PHARMACIE & STOCK</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Stock pharmacie</h1>
          <p className="text-slate-500 mt-1">{items.length} référence(s) en stock</p>
        </div>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <Button onClick={() => setOpenAdd(true)} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="open-add-item"><Plus className="h-4 w-4 mr-2" />Ajouter un article</Button>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvel article pharmacie</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Doliprane 500mg" data-testid="item-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="item-cat"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(CATEGORIES).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Unité</Label><Input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} placeholder="comprimé, ml, sachet..." data-testid="item-unit" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Quantité initiale</Label><Input type="number" value={form.qty} onChange={e => setForm({...form, qty: e.target.value})} data-testid="item-qty" /></div>
                <div><Label>Seuil d&apos;alerte</Label><Input type="number" value={form.alert_threshold} onChange={e => setForm({...form, alert_threshold: e.target.value})} data-testid="item-threshold" /></div>
              </div>
              <div><Label>Date de péremption</Label><Input type="date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} data-testid="item-expiry" /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} data-testid="item-notes" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenAdd(false)}>Annuler</Button>
              <Button onClick={create} disabled={saving} className="bg-[#166534] text-white" data-testid="save-item">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Article</th>
                <th className="px-4 py-3">Catégorie</th>
                <th className="px-4 py-3 text-right">Stock</th>
                <th className="px-4 py-3">Péremption</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={i.id} className={`border-t border-slate-100 hover:bg-slate-50 ${i.low_stock || i.expired ? "bg-red-50/30" : ""}`} data-testid={`item-${i.id}`}>
                  <td className="px-4 py-3 font-semibold">{i.name}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{CATEGORIES[i.category]}</span></td>
                  <td className={`px-4 py-3 text-right tabular-nums font-bold ${i.low_stock ? "text-[#EA580C]" : "text-[#1E3A8A]"}`}>{i.qty} {i.unit}</td>
                  <td className="px-4 py-3 text-xs">{i.expiry_date || "-"}{i.days_to_expiry != null && i.days_to_expiry < 60 && <div className="text-red-600 font-bold">{i.days_to_expiry < 0 ? `Expiré (${-i.days_to_expiry}j)` : `Dans ${i.days_to_expiry}j`}</div>}</td>
                  <td className="px-4 py-3">
                    {i.expired && <span className="inline-block px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-bold mr-1"><AlertTriangle className="inline h-3 w-3 mr-0.5" />Expiré</span>}
                    {i.low_stock && <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs font-bold">Stock bas</span>}
                    {!i.expired && !i.low_stock && <span className="text-xs text-green-600">✓ OK</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openMovement(i, "entree")} title="Entrée" data-testid={`mv-in-${i.id}`}><ArrowUpRight className="h-4 w-4 text-green-600" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openMovement(i, "sortie")} title="Sortie" data-testid={`mv-out-${i.id}`}><ArrowDownRight className="h-4 w-4 text-orange-600" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Aucun article en stock. Cliquez sur &laquo;&nbsp;Ajouter un article&nbsp;&raquo;.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Movement dialog */}
      <Dialog open={openMove} onOpenChange={setOpenMove}>
        <DialogContent>
          <DialogHeader><DialogTitle>{moveForm.type === "entree" ? "Entrée" : moveForm.type === "sortie" ? "Sortie" : "Ajustement"} de stock — {moveItem?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">Stock actuel : <strong>{moveItem?.qty} {moveItem?.unit}</strong></div>
            <div><Label>Quantité</Label><Input type="number" min="0" value={moveForm.qty} onChange={e => setMoveForm({...moveForm, qty: e.target.value})} data-testid="mv-qty" /></div>
            <div><Label>Motif / Élève bénéficiaire</Label><Input value={moveForm.reason} onChange={e => setMoveForm({...moveForm, reason: e.target.value})} placeholder="Achat, soin à Mariama Diallo..." data-testid="mv-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenMove(false)}>Annuler</Button>
            <Button onClick={doMovement} disabled={saving} className={moveForm.type === "entree" ? "bg-green-600 text-white" : "bg-orange-600 text-white"} data-testid="mv-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Valider</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
